<script>
    import Icon from '$component/Icon.svelte';
// @ts-nocheck

    import {initializeBasket, items, loading, store as basketStore, refreshBasket, resetOrderRemark} from '$lib/basketStore.svelte.js';
    import {fetchUser, store as userStore} from '$lib/userStore.svelte.js';
    import {fetchShippingAndPayment, selectShipping, selectPayment, paymentStore, handleGatewayApproved, cancelStripeExpress} from '$lib/paymentStore.svelte.js';
    import {sessionStore, checkSession, markExpectedAlive, resetSession} from '$lib/sessionStore.svelte.js';
    import {t} from '$utils/lang.js';
    import {checkoutAddressesComplete} from '$lib/addressValidation.js';
    import Basket from "$steps/Basket.svelte";
    import User from "$steps/User.svelte";
    import Payment from "$steps/Payment.svelte";
    import Order from "$steps/Order.svelte";
    import ThankYou from "$steps/ThankYou.svelte";
    import ReviewHeadline from "$component/ReviewHeadline.svelte";
    import {PHONE_REGEX} from '$component/PhoneSection.svelte';
    import {trackViewCart, trackBeginCheckout, trackAddShippingInfo, trackAddPaymentInfo} from '$lib/tracking.js';
    import { onMount } from 'svelte';

    import { devLog as _devLog } from '$utils/devLog.js';
    const devLog = (tag, ...args) => _devLog(`Checkout:${tag}`, ...args);

    const STEP_ORDER = ['basket', 'user', 'payment', 'order'];

    // Hash ↔ step mapping (hash = section id on the rendered element)
    const HASH_TO_STEP = {basket: 'basket', contact: 'user', payment: 'payment', review: 'order', thankyou: 'thankyou', user: 'user', order: 'order'};
    const STEP_TO_HASH = {basket: 'basket', user: 'contact', payment: 'payment', order: 'review', thankyou: 'thankyou'};

    function parseHash() {
        const raw = (typeof window !== 'undefined' && window.location.hash || '').replace(/^#/, '');
        const [hashPart, ...rest] = raw.split('/');
        const step = HASH_TO_STEP[hashPart] ?? 'basket';
        const sub = rest.join('/') || '';
        return {step, sub};
    }

    const initial = parseHash();
    // Capture URL params before Svelte $effects run and strip them via replaceState.
    const _initialUrlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    // When returning from Express checkout, show a loading screen until we know
    // whether phone is required (payment step) or not (order step).
    // paypalReady=express → returning from a PayPal Express re-approval (#49):
    // land directly on the order step so Order.svelte's return handler retries
    // placeOrder (capture). amazonReady=express uses a loading screen first.
    const _initialStep = _initialUrlParams.get('amazonReady') === 'express'
        ? 'express-loading'
        : _initialUrlParams.get('paypalReady') === 'express'
            ? 'order'
            : initial.step;
    let checkoutStep = $state(_initialStep);
    let userSubRoute = $state(initial.sub); // e.g. 'change', 'new', 'edit-billing'
    let orderId = $state('');
    let isLeasingOrder = $state(false);
    let expressLoginRequired = $state(false);
    let nonShippableCountry = $state(false);
    let nonShippableCountryName = $state('');
    let easycreditAborted = $state(false);
    // Warenkorb hat sich nach der easyCredit-Freigabe geändert → der Vorgang
    // ist wertlos. Eigener Top-Level-State statt paymentStore.error, weil
    // fetchShippingAndPayment() beim Aktivieren des Payment-Steps store.error
    // auf null setzt und die Meldung sonst sofort wieder verschwindet.
    let easycreditBasketChanged = $state(false);
    const expressMode = $derived(
        (paymentStore.paymentMethods ?? []).find(p => p.id === paymentStore.selectedPaymentId)?.type === 'EXPRESS'
    );
    const allowAddressChange = $derived(
        !paymentStore.loading &&
        (paymentStore.paymentMethods ?? []).find(p => p.id === paymentStore.selectedPaymentId)
            ?.metadata?.allowAddressChange !== false
    );
    let root;
    let addErrorTimer;
    $effect(() => {
        if (basketStore.addError) {
            clearTimeout(addErrorTimer);
            addErrorTimer = setTimeout(() => { basketStore.addError = null; }, 5000);
        }
    });

    function getStatus(step) {
        const currentIdx = STEP_ORDER.indexOf(checkoutStep);
        const stepIdx = STEP_ORDER.indexOf(step);
        if (stepIdx === currentIdx) return 'active';
        if (stepIdx < currentIdx) return 'completed';
        return 'upcoming';
    }

    $effect(() => {
        devLog('step', {checkoutStep});
    });

    // GA4 funnel events — fire each once when its step is first reached and the
    // basket has loaded. Mirrors the legacy ys_tracking checkout_steps_main.tpl
    // (view_cart / begin_checkout / add_shipping_info / add_payment_info).
    const _trackedSteps = new Set();
    $effect(() => {
        const step = checkoutStep;
        if (basketStore.items.size === 0) return; // wait for basket to load
        if (step === 'basket' && !_trackedSteps.has(step)) { _trackedSteps.add(step); trackViewCart(); }
        else if (step === 'user' && !_trackedSteps.has(step)) { _trackedSteps.add(step); trackBeginCheckout(); }
        else if (step === 'payment' && !_trackedSteps.has(step)) { _trackedSteps.add(step); trackAddShippingInfo(); }
        else if (step === 'order' && !_trackedSteps.has(step)) { _trackedSteps.add(step); trackAddPaymentInfo(); }
    });

    // Explicit $derived so checkoutStep changes reliably propagate to step components
    const basketStatus = $derived(getStatus('basket'));
    const userStatus = $derived(getStatus('user'));
    const paymentStatus = $derived(getStatus('payment'));
    const orderStatus = $derived(getStatus('order'));

    // Desktop 2-col mode: user/payment/order render as left col, basket (summary) sticky right.
    // Basket step itself uses full width (has its own internal 2-col layout).
    const twoColMode = $derived(checkoutStep !== 'basket' && checkoutStep !== 'thankyou' && checkoutStep !== 'express-loading');

    // Scroll the newly-active step to the top on a step change. Without this,
    // advancing from the bottom of one step (e.g. the "Weiter zur Überprüfung"
    // button) leaves the viewport scrolled down — the user lands in the footer
    // instead of at the new step. Skip the first run so a reload / deep-link
    // keeps the browser's natural position.
    let _firstStepRun = true;
    $effect(() => {
        const step = checkoutStep;
        if (typeof window === 'undefined') return;
        if (_firstStepRun) { _firstStepRun = false; return; }
        const id = STEP_TO_HASH[step] ?? step;
        requestAnimationFrame(() => requestAnimationFrame(() => {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({behavior: 'smooth', block: 'start'});
            else window.scrollTo({top: 0, behavior: 'smooth'});
        }));
    });

    // Sync step → URL without triggering browser scroll-to-fragment.
    // All step IDs are always in the DOM, so browser scroll is correct — no save/restore needed.
    $effect(() => {
        if (typeof window === 'undefined') return;
        const hash = STEP_TO_HASH[checkoutStep] ?? checkoutStep;
        const sub = checkoutStep === 'user' && userSubRoute ? `/${userSubRoute}` : '';
        const target = `#${hash}${sub}`;
        if (window.location.hash !== target) {
            window.history.replaceState(null, '', target);
        }
    });

    // Strip transient checkout-flow params (error / *Ready / *Aborted) from the
    // URL but KEEP the rest — notably cl=basket, so a reload or browser-back
    // still routes through BasketController into the SPA. Dropping the whole
    // query (pathname-only) sent the user to the OXID start page on reload.
    function cleanFlowParams() {
        if (typeof window === 'undefined') return;
        const url = new URL(window.location.href);
        ['error', 'amazonReady', 'easycreditReady', 'easycreditAborted'].forEach(k => url.searchParams.delete(k));
        const qs = url.searchParams.toString();
        history.replaceState(null, '', url.pathname + (qs ? `?${qs}` : '') + url.hash);
    }

    // Auto-redirect countdown for the expired-session banner. Starts at 5s
    // (gives the user a moment to read the message) and decrements while the
    // banner is visible. Hitting 0 triggers a soft reload that lands on the
    // basket step with a freshly-minted PHP session.
    let expiryCountdown = $state(5);
    let _expiryTimer = null;

    function dismissExpiryBanner() {
        if (_expiryTimer) { clearInterval(_expiryTimer); _expiryTimer = null; }
        // Forget the previous probe result so a manual return to basket
        // (which will mint a new session) doesn't stay stuck on `false`.
        resetSession();
        if (typeof window !== 'undefined') {
            // Hard navigation to /index.php?cl=basket — same path Apache uses
            // when it spots an expired session. Mints a fresh sid cookie +
            // empty basket, then the SPA boots back into the basket step.
            // location.replace() (not reload) is mandatory: reload() KEEPS the
            // current #hash, so a URL like ?cl=basket#contact would re-boot the
            // SPA into the contact step against the dead session → reload loop.
            // replace() overwrites path AND hash and leaves no history entry
            // for the expired-session page.
            window.location.replace('/index.php?cl=basket');
        }
    }

    // Feed the session-store's sticky `expectedAlive` marker from local UI
    // state. As soon as we see the user logged in OR the basket holding
    // items, we lock in the expectation that this session SHOULD remain
    // alive. The store then uses it to distinguish "anonymous user with
    // empty cart just landed" (alive=true ok) from "session got recreated
    // empty after we had stuff" (alive=true but recreated → treat as dead).
    //
    // Bonus: if the user landed directly on a non-basket step via URL hash
    // (e.g. tab-restore on #review after PHP killed the session), we KNOW
    // the session was supposed to be alive — nobody navigates straight to
    // review without items. Lock the marker right away so the first probe
    // result can flag the recreated-empty state correctly.
    if (_initialStep !== 'basket' && _initialStep !== 'thankyou' && _initialStep !== 'express-loading') {
        markExpectedAlive();
    }
    $effect(() => {
        if (userStore.user?.isLoggedIn || basketStore.items.size > 0) {
            markExpectedAlive();
        }
    });

    // Whenever the backend tells us the session is dead, start the countdown.
    // Skip the countdown when the user is already on the basket step — the
    // empty-cart guard below will route them out naturally without nagging.
    $effect(() => {
        if (sessionStore.alive === false && checkoutStep !== 'basket') {
            if (_expiryTimer) return; // already running
            expiryCountdown = 5;
            _expiryTimer = setInterval(() => {
                expiryCountdown -= 1;
                if (expiryCountdown <= 0) dismissExpiryBanner();
            }, 1000);
        } else if (sessionStore.alive !== false && _expiryTimer) {
            // Session came back (unlikely but possible after a tab-focus
            // re-probe if PHP regenerated mid-flight). Cancel countdown.
            clearInterval(_expiryTimer);
            _expiryTimer = null;
        }
    });

    // Empty-cart guard: if we end up on a non-basket step with an empty cart
    // after a session check, route the user back to the basket. Re-enabled
    // now that dismissExpiryBanner() uses location.replace() WITHOUT the hash
    // — the previous reload loop was the hash-preserve bug (reload() kept
    // #contact, the SPA re-booted the non-basket step, the cart was still
    // empty and the guard fired again). After replace() the page reloads on
    // the bare basket URL → checkoutStep === 'basket' → this guard early-
    // returns, so no loop.
    $effect(() => {
        if (basketStore.loading) return;
        if (basketStore.items.size > 0) return;
        if (checkoutStep === 'basket' || checkoutStep === 'thankyou' || checkoutStep === 'express-loading') return;
        if (!sessionStore.lastCheck) return;
        dismissExpiryBanner();
    });

    // Load the basket data when the component is first created.
    onMount(() => {
        initializeBasket();

        // Probe the session immediately so a tab that's been sitting on
        // user/payment/order for hours finds out it's dead before rendering
        // the stale step UI.
        checkSession();

        // After a page reload without URL params (e.g. F5 on user/payment/order step),
        // no URL handler triggers fetchShippingAndPayment. Load it now so paymentStore
        // (selectedPaymentId, allowAddressChange) reflects the actual session state.
        if (checkoutStep !== 'basket' && checkoutStep !== 'thankyou') {
            fetchShippingAndPayment();
        }

        // Re-probe when the tab regains focus. Visibilitychange covers
        // background→foreground; focus covers click-into-window after
        // alt-tab. Belt-and-braces because browsers differ in which one
        // fires for which interaction.
        const reprobe = () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
                checkSession();
            }
        };
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', reprobe);
            window.addEventListener('focus', reprobe);
        }

        // Use params captured at script-body time — by the time onMount runs,
        // the $effect for hash sync has already called replaceState and stripped them.
        const urlParams = _initialUrlParams;
        const urlError = urlParams.get('error');
        const amazonErrors = {
            amazon_canceled: 'Du hast die Amazon Pay Zahlung abgebrochen. Bitte wähle eine andere Zahlungsart.',
            amazon_declined: 'Amazon Pay hat die Zahlung abgelehnt. Bitte wähle eine andere Zahlungsart.',
            amazon_rejected: 'Amazon konnte die Zahlung nicht abschließen. Bitte wähle eine andere Zahlungsart.',
        };
        if (urlError && amazonErrors[urlError]) {
            paymentStore.error = t(amazonErrors[urlError]);
            cleanFlowParams();
        } else if (urlError === 'ec_basket_changed') {
            // MT-4: EasyCredit-Return-Pfad hat Basket-Total-Mismatch erkannt.
            paymentStore.error = t('Dein Warenkorb hat sich geändert. Bitte starte EasyCredit erneut.');
            cleanFlowParams();
        }

        const amazonReady = urlParams.get('amazonReady');
        if (amazonReady === 'express') {
            cleanFlowParams();
            (async () => {
                await Promise.all([fetchUser(), fetchShippingAndPayment(), refreshBasket()]);
                handleGatewayApproved();

                // Snapshot des Express-SCA-Totals. Amazon hat genau diesen
                // Betrag im checkoutSession gelocked — wenn der Basket-Total
                // später abweicht (z.B. Versandart-Wechsel im Payment-Step),
                // muss client-seitig auf m7_amazonpay (regular) geswitcht
                // werden, sonst failt completeCheckoutSession mit 409
                // AmountMismatch.
                paymentStore.amazonExpressLockedAmount = basketStore.snapshot?.total?.brutto ?? basketStore.price?.brutto ?? null;
                devLog('amazonReady:express:locked', {amount: paymentStore.amazonExpressLockedAmount});

                // Block Express if user already has a password-protected account.
                if (userStore.user?.hasPassword) {
                    devLog('amazonReady:express:blocked', {email: userStore.user.email});
                    await selectPayment('');
                    expressLoginRequired = true;
                    checkoutStep = 'user';
                    return;
                }

                // Non-shippable destination (e.g. a US PayPal address): the
                // backend returns zero shipping methods for the country. Stop at
                // step 1 with a clear banner instead of letting the user slip
                // through to the review step + a generic PayPal error.
                if ((paymentStore.shippingMethods ?? []).length === 0) {
                    nonShippableCountryName = userStore.user?.shippingAddress?.country
                        || userStore.user?.billingAddress?.country || '';
                    nonShippableCountry = true;
                    checkoutStep = 'user';
                    return;
                }
                if (!paymentStore.selectedShippingId && paymentStore.shippingMethods.length > 0) {
                    await selectShipping(paymentStore.shippingMethods[0].shippingSet.id);
                }
                // Resync the basket so the sidebar shows the resolved shipping
                // cost + total. selectShipping() does NOT refresh the basket, and
                // the initial refreshBasket above ran before shipping was known.
                await refreshBasket();
                const selShipping = paymentStore.shippingMethods.find(s => s.shippingSet?.id === paymentStore.selectedShippingId);
                const selPayment = (paymentStore.paymentMethods ?? []).find(p => p.id === paymentStore.selectedPaymentId);
                // If any available shipping requires phone, send user through payment step.
                // Covers the case where DHL is auto-selected but Emons (requiresPhone) is also available.
                const needsPhone = (paymentStore.shippingMethods ?? []).some(s => s.shippingSet?.requiresPhone);
                const phone = userStore.user?.billingAddress?.phone || userStore.user?.shippingAddress?.phone || '';
                const phoneMissing = needsPhone && !PHONE_REGEX.test(phone);
                // Wunsch-Zustelldatum: gleicher Detour wie Phone — wenn Set+Payment
                // ys_use_shipping_date=1 unterstützen, gönnen wir dem Kunden den
                // Payment-Step damit er optional ein Datum wählen kann.
                const canSpecifyDate = !!selShipping?.shippingSet?.useShippingDate && !!selPayment?.useShippingDate;
                const goPayment = phoneMissing || canSpecifyDate;
                devLog('amazonReady:express', {needsPhone, phoneMissing, canSpecifyDate, selShipping: selShipping?.shippingSet?.id, selPayment: selPayment?.id, nextStep: goPayment ? 'payment' : 'order'});
                checkoutStep = goPayment ? 'payment' : 'order';
            })();
        }

        const easycreditReady = urlParams.get('easycreditReady');
        if (easycreditReady === '1') {
            cleanFlowParams();
            (async () => {
                await Promise.all([fetchUser(), fetchShippingAndPayment(), refreshBasket()]);
                handleGatewayApproved();
                checkoutStep = 'order';
            })();
        }

        if (urlParams.get('easycreditAborted') === '1') {
            cleanFlowParams();
            easycreditAborted = true;
            (async () => {
                await fetchShippingAndPayment();
                checkoutStep = 'payment';
            })();
        }

        const onHashChange = () => {
            const next = parseHash();
            // Update sub-route FIRST so a step change that triggers a remount
            // (e.g. payment → user) sees the right initial subRoute prop value.
            if (next.sub !== userSubRoute) userSubRoute = next.sub;
            if (next.step !== checkoutStep) checkoutStep = next.step;
        };
        window.addEventListener('hashchange', onHashChange);
        return () => {
            window.removeEventListener('hashchange', onHashChange);
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', reprobe);
                window.removeEventListener('focus', reprobe);
            }
            if (_expiryTimer) clearInterval(_expiryTimer);
        };
    });

    // Redirect to basket if Akku articles are mixed with non-Akku articles.
    $effect(() => {
        if (loading()) return;
        if (basketStore.hasAkkuMix && checkoutStep !== 'basket' && checkoutStep !== 'express-loading') {
            checkoutStep = 'basket';
        }
    });

    // Guard: incomplete address → redirect to user address step
    // (skip during initial load, on basket/user/thankyou/express-loading steps, and while express mode is active)
    // decision: [1]
    $effect(() => {
        if (loading() || userStore.loading || paymentStore.loading) return;
        if (checkoutStep === 'basket' || checkoutStep === 'user' || checkoutStep === 'thankyou' || checkoutStep === 'express-loading') return;
        if (expressMode) return;
        if (!checkoutAddressesComplete(userStore.user)) {
            devLog('guard:address-incomplete', {step: checkoutStep});
            checkoutStep = 'user';
        }
    });

    // Guard: Amazon Express SCA locked the checkoutSession to a specific
    // amount. Wenn der Basket-Total sich nachträglich davon unterscheidet
    // (Versandart geändert, Wunschdatum-Detour usw.), würde completeCheckoutSession
    // mit HTTP 422 reasonCode=AmountMismatch failen. Client-seitig vorab auf m7_amazonpay
    // (regular) switchen: die regular Variante erzeugt beim Render auf dem
    // Order-Step automatisch eine frische checkoutSession mit aktuellem Total
    // → Buyer klickt einmal auf den Amazon-Pay-Button und kommt sauber durch.
    $effect(() => {
        const locked = paymentStore.amazonExpressLockedAmount;
        if (locked == null) return;
        const current = basketStore.snapshot?.total?.brutto ?? basketStore.price?.brutto;
        if (current == null) return;
        const selected = paymentStore.selectedPaymentId;
        if (selected !== 'm7_amazonpay_express') return;
        // Cent-Round, damit Float-Wackler nicht triggern
        if (Math.round(current * 100) === Math.round(locked * 100)) return;
        devLog('amazonExpress:amountMismatch:autoSwap', {locked, current});
        paymentStore.amazonExpressLockedAmount = null; // einmalig swappen
        (async () => {
            try {
                // 1. Pending Express-Auth bei Amazon canceln — sonst bleibt
                //    eine "Autorisiert - Abbuchung erforderlich" Charge stehen
                //    während die regular Variante eine zweite Auth erzeugt.
                //    AWAIT: erst wenn Amazon serverseitig die Express-Session
                //    geschlossen hat, darf die regular Variante eine neue
                //    aufmachen — sonst race condition mit zwei Open Sessions.
                try {
                    await fetch('/index.php?cl=m7-amazonpay-api&fnc=cancelExpress', {
                        method: 'POST',
                        credentials: 'include',
                    });
                } catch (e) {
                    devLog('amazonExpress:cancelExpress:failed', {error: e.message});
                }
                // 2. Lokal auf regular Variante swappen
                await selectPayment('m7_amazonpay');
            } catch (e) {
                devLog('amazonExpress:autoSwap:failed', {error: e.message});
            }
        })();
    });

    // Guard: Stripe wallet express reserved a card amount. A lower total is fine (the order captures only what it is worth), a
    // higher one (shipping, insurance, delivery type) cannot be captured, so release the reservation and let the customer
    // confirm the new amount with the normal card payment. Leaving the express payment releases it as well. reference: [Component/Payments/StripeExpress 2]
    // stripeExpressSeen: the express payment was really selected once; before that the session payment is still the old one
    // (the approve has just returned) and must not count as "left".
    let stripeExpressSeen = false;
    $effect(() => {
        const locked = paymentStore.stripeExpressLockedAmount;
        if (locked == null) { stripeExpressSeen = false; return; }
        const selected = paymentStore.selectedPaymentId;
        if (selected === 'm7_stripe_express') {
            stripeExpressSeen = true;
            const current = basketStore.snapshot?.total?.brutto ?? basketStore.price?.brutto;
            if (current == null || Math.round(current * 100) <= Math.round(locked * 100)) return;
            devLog('stripeExpress:amountIncreased:autoSwap', {locked, current});
            (async () => {
                await cancelStripeExpress(`amount_increased locked=${locked} current=${current}`);
                try {
                    await selectPayment('m7_stripe_card');
                    paymentStore.error = t('Der Betrag hat sich geändert. Bitte bestätige die Zahlung mit deiner Karte erneut.');
                } catch (e) {
                    devLog('stripeExpress:autoSwap:failed', {error: e.message});
                }
            })();
            return;
        }
        if (!stripeExpressSeen || paymentStore.paymentMethods.length === 0) return;
        stripeExpressSeen = false;
        devLog('stripeExpress:left', {selected});
        cancelStripeExpress(`left selected=${selected}`);
    });

    // Guard: on order step with no valid payment or no shipping → redirect to payment step
    $effect(() => {
        if (checkoutStep !== 'order') return;
        if (paymentStore.paymentMethods.length === 0) return; // still loading
        const validPayment = paymentStore.paymentMethods.find(p => p.id === paymentStore.selectedPaymentId);
        if (!validPayment || !paymentStore.selectedShippingId) {
            checkoutStep = 'payment';
        }
    });

</script>

{#if basketStore.addError}
    <div class="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-sm w-[calc(100%-2rem)] bg-white border border-scarlet/40 rounded-lg shadow-lg px-4 py-3 flex items-start gap-3" role="alert" data-checkout-section="add-error-toast">
        <svg class="shrink-0 mt-0.5 size-5 text-scarlet" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-.75-10.5a.75.75 0 011.5 0v4a.75.75 0 01-1.5 0v-4zm.75 7a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/></svg>
        <div class="text-sm">
            <p class="font-bold text-gray-900">{t('Artikel konnte nicht hinzugefügt werden')}</p>
            <p class="text-gray-600 mt-0.5">{t(basketStore.addError)}</p>
        </div>
        <button aria-label={t('Schließen')} onclick={() => basketStore.addError = null} class="ml-auto shrink-0 text-gray-400 hover:text-gray-600 cursor-pointer">
            <Icon name="times" class="size-4"/>
        </button>
    </div>
{/if}

{#if sessionStore.alive === false && checkoutStep !== 'basket' && checkoutStep !== 'thankyou'}
    <!-- Session-expired overlay. Triggered when the backend reports the redis
         PHP session key is gone (typically: tab open overnight). Auto-redirects
         to a fresh basket after 5 s; user can also click "Zum Warenkorb" early. -->
    <div class="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center px-4" role="alertdialog" aria-modal="true" aria-labelledby="session-expired-title" data-checkout-section="session-expired">
        <div class="bg-white rounded-md shadow-xl max-w-md w-full px-5.5 py-5.5 flex flex-col gap-4">
            <div class="flex items-start gap-3">
                <svg class="shrink-0 mt-0.5 size-6 text-scarlet" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-.75-10.5a.75.75 0 011.5 0v4a.75.75 0 01-1.5 0v-4zm.75 7a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/>
                </svg>
                <div class="flex flex-col gap-1">
                    <h2 id="session-expired-title" class="font-bold text-base text-gray-900">{t('Deine Sitzung ist abgelaufen')}</h2>
                    <p class="text-sm text-gray-600">{t('Aus Sicherheitsgründen wurdest du nach längerer Inaktivität abgemeldet. Bitte starte deinen Checkout neu — dein Warenkorb wird gleich aktualisiert.')}</p>
                </div>
            </div>
            <button
                type="button"
                onclick={dismissExpiryBanner}
                class="self-stretch bg-cyprus text-white rounded-md px-4 py-2.5 text-sm font-bold cursor-pointer hover:bg-cyprus/90"
                data-checkout-action="session-expired-restart"
            >{t('Zum Warenkorb')}{expiryCountdown > 0 ? ` (${expiryCountdown})` : ''}</button>
        </div>
    </div>
{/if}

{#if easycreditBasketChanged}
    <div class="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-sm w-[calc(100%-2rem)] bg-white border border-scarlet/40 rounded-lg shadow-lg px-4 py-3 flex items-start gap-3" role="alert" data-checkout-section="easycredit-basket-changed-toast">
        <svg class="shrink-0 mt-0.5 size-5 text-scarlet" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-.75-10.5a.75.75 0 011.5 0v4a.75.75 0 01-1.5 0v-4zm.75 7a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/></svg>
        <div class="text-sm">
            <p class="font-bold text-gray-900">{t('Warenkorb geändert')}</p>
            <!-- Produktneutral: gilt für Ratenkauf und Rechnungskauf gleichermaßen. -->
            <p class="text-gray-600 mt-0.5">{t('Bitte wähle easyCredit erneut, damit wir den Betrag aktualisieren können.')}</p>
        </div>
        <button aria-label={t('Schließen')} onclick={() => easycreditBasketChanged = false} class="ml-auto shrink-0 text-gray-400 hover:text-gray-600 cursor-pointer">
            <svg class="size-4" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
        </button>
    </div>
{/if}

{#if easycreditAborted}
    <div class="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-sm w-[calc(100%-2rem)] bg-white border border-scarlet/40 rounded-lg shadow-lg px-4 py-3 flex items-start gap-3" role="alert" data-checkout-section="easycredit-aborted-toast">
        <svg class="shrink-0 mt-0.5 size-5 text-scarlet" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-.75-10.5a.75.75 0 011.5 0v4a.75.75 0 01-1.5 0v-4zm.75 7a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/></svg>
        <div class="text-sm">
            <p class="font-bold text-gray-900">{t('easyCredit-Ratenkauf abgebrochen')}</p>
            <p class="text-gray-600 mt-0.5">{t('Bitte wähle eine andere Zahlungsart.')}</p>
        </div>
        <button aria-label={t('Schließen')} onclick={() => easycreditAborted = false} class="ml-auto shrink-0 text-gray-400 hover:text-gray-600 cursor-pointer">
            <Icon name="times" class="size-4"/>
        </button>
    </div>
{/if}

<div bind:this={root} class="bg-grey tracking-normal">
    <div class="container mx-auto px-3.5 py-4 lg:max-w-7xl" data-checkout-step={checkoutStep}>
        <div class="flex flex-col gap-4"
             class:lg:flex-row={twoColMode}
             class:lg:items-start={twoColMode}
             class:lg:gap-4={twoColMode}>
            {#if checkoutStep !== 'thankyou'}
            {#if checkoutStep === 'order'}
                <!-- Mobile-only review headline: sits above the summary in the
                     mobile source order (desktop shows it inside the left column). -->
                <ReviewHeadline class="lg:hidden" />
            {/if}
            <div id="basket"
                 class:lg:w-95={twoColMode}
                 class:lg:order-2={twoColMode}
                 class:lg:sticky={twoColMode}
                 class:lg:top-4={twoColMode}
                 class:lg:shrink-0={twoColMode}>
                <Basket
                    status={basketStatus}
                    newSummary={checkoutStep === 'order'}
                    onContinue={() => checkoutStep = 'user'}
                    onActivate={() => checkoutStep = 'basket'}
                    onExpressApproved={async () => {
                        await Promise.all([fetchUser(), fetchShippingAndPayment()]);
                        // expressMode auto-derives from paymentStore.selectedPaymentId === 'oscpaypal_express'

                        devLog('onExpressApproved', {
                            selectedShippingId: paymentStore.selectedShippingId,
                            shippingCount: paymentStore.shippingMethods.length,
                            selectedPaymentId: paymentStore.selectedPaymentId,
                            hasPassword: userStore.user?.hasPassword,
                        });

                        // Block Express if user already has a password-protected account.
                        // They must log in so the order is linked to their existing account.
                        // Stripe wallet: the card was just reserved, release it at once so nothing is left hanging (the guard
                        // below would do the same when the payment is cleared, this does not depend on that timing).
                        // reference: [Component/Payments/StripeExpress 3]
                        if (userStore.user?.hasPassword) {
                            devLog('onExpressApproved:blocked', {email: userStore.user.email});
                            if (paymentStore.stripeExpressLockedAmount != null) await cancelStripeExpress('login_required');
                            await selectPayment('');
                            expressLoginRequired = true;
                            checkoutStep = 'user';
                            return;
                        }

                        // After Express login PHP regenerates the session → sShipSet lost.
                        // Auto-select first available shipping if none is selected.
                        // Non-shippable destination (e.g. a US PayPal address):
                        // zero shipping methods for the country. Stop at step 1
                        // with a clear banner instead of slipping to review.
                        if ((paymentStore.shippingMethods ?? []).length === 0) {
                            nonShippableCountryName = userStore.user?.shippingAddress?.country
                                || userStore.user?.billingAddress?.country || '';
                            nonShippableCountry = true;
                            checkoutStep = 'user';
                            return;
                        }

                        if (!paymentStore.selectedShippingId && paymentStore.shippingMethods.length > 0) {
                            devLog('onExpressApproved', 'auto-selecting shipping:', paymentStore.shippingMethods[0].shippingSet.id);
                            await selectShipping(paymentStore.shippingMethods[0].shippingSet.id);
                        }

                        // Resync the basket so the sidebar reflects the resolved
                        // shipping cost + total. The PayPal Express return never
                        // refreshed the basket (only fetchUser + shipping/payment),
                        // and selectShipping() doesn't refresh it — so the sidebar
                        // kept the pre-address default (e.g. 11.90 Spedition).
                        await refreshBasket();

                        // Go to payment step only if phone is required but missing
                        // OR wenn Set+Payment ein Wunsch-Zustelldatum erlauben.
                        const selShipping = paymentStore.shippingMethods.find(
                            s => s.shippingSet?.id === paymentStore.selectedShippingId
                        );
                        const selPayment = (paymentStore.paymentMethods ?? []).find(
                            p => p.id === paymentStore.selectedPaymentId
                        );
                        const needsPhone = !!selShipping?.shippingSet?.requiresPhone;
                        const phone = userStore.user?.billingAddress?.phone
                            ?? userStore.user?.shippingAddress?.phone ?? '';
                        const phoneMissing = needsPhone && !PHONE_REGEX.test(phone);
                        const canSpecifyDate = !!selShipping?.shippingSet?.useShippingDate && !!selPayment?.useShippingDate;
                        const goPayment = phoneMissing || canSpecifyDate;

                        devLog('onExpressApproved', {selShipping: selShipping?.shippingSet?.id, selPayment: selPayment?.id, needsPhone, phoneMissing, canSpecifyDate, nextStep: goPayment ? 'payment' : 'order'});
                        checkoutStep = goPayment ? 'payment' : 'order';
                    }}
                />
            </div>
            {#if checkoutStep !== 'basket'}
            <div class="flex flex-col gap-4"
                 class:lg:flex-1={twoColMode}
                 class:lg:order-1={twoColMode}
                 class:lg:min-w-0={twoColMode}>
                <div id="contact" class:hidden={checkoutStep === 'order'}>
                    <User
                        status={userStatus}
                        onContinue={() => checkoutStep = 'payment'}
                        onActivate={() => checkoutStep = 'user'}
                        bind:subRoute={userSubRoute}
                        isExpressMode={expressMode}
                        allowAddressChange={allowAddressChange}
                        expressLoginRequired={expressLoginRequired}
                        onExpressLoginComplete={() => { expressLoginRequired = false; }}
                        nonShippableCountry={nonShippableCountry}
                        nonShippableCountryName={nonShippableCountryName}
                        onAddressChanged={() => { nonShippableCountry = false; }}
                    />
                </div>
                <div id="payment" class:hidden={checkoutStep === 'order'}>
                    <Payment
                        status={paymentStatus}
                        isExpressMode={expressMode}
                        onContinue={() => checkoutStep = 'order'}
                        onActivate={() => checkoutStep = 'payment'}
                        onBack={() => checkoutStep = 'user'}
                    />
                </div>
                <div id="review">
                    <Order
                        status={orderStatus}
                        onContinue={(id, leasing) => {
                            orderId = id ?? '';
                            isLeasingOrder = !!leasing;
                            checkoutStep = 'thankyou';
                            // Order placed → backend consumed the session ordrem;
                            // clear the local remark so a new same-tab order is blank.
                            resetOrderRemark();
                            // Refetch user state after order — guest checkout can flip
                            // hasPassword/isLoggedIn server-side, but our local store
                            // still reflects the pre-order snapshot otherwise.
                            fetchUser().catch(() => {});
                        }}
                        onBack={(step) => checkoutStep = step}
                        onEasyCreditStale={() => easycreditBasketChanged = true}
                    />
                </div>
            </div>
            {/if}
            {/if}

            {#if checkoutStep === 'express-loading'}
                <div class="bg-white rounded-md px-5.5 py-10 flex flex-col items-center gap-4 text-sm text-grey-medium" data-checkout-step="express-loading">
                    <svg class="animate-spin size-8 text-elm" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                    <span>{t('Deine Bestellung wird vorbereitet…')}</span>
                </div>
            {/if}

            {#if checkoutStep === 'thankyou'}
                <div id="thankyou">
                    <ThankYou {orderId} isLeasing={isLeasingOrder} isGuest={!!userStore.user && !userStore.user.hasPassword}/>
                </div>
            {/if}
        </div>
    </div>
</div>
